import { isAxiosError } from 'axios'
import Cookie from 'js-cookie'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { apiV2 } from 'src/services/api'
import { getToken } from 'src/services/auth'
import { Carrinho } from 'src/types/Carrinho'
import { SHOPPING_CART_COOKIE, getDomainName } from 'src/util/carrinho'
import { montarObjetoGoogle } from 'src/util/googleObjectBuilder'
import * as gtg from '../../services/gtag'
import * as gtm from '../../services/gtm'
import * as Sentry from '@sentry/nextjs'

export const removerCarrinho = () =>
  Cookie.remove(SHOPPING_CART_COOKIE, { domain: getDomainName() })

export const recuperarCarrinho = () => Cookie.get(SHOPPING_CART_COOKIE) ?? null

const salvarCarrinho = (id: string) => {
  const tempoExpiracaoCarrinho = new Date()
  tempoExpiracaoCarrinho.setDate(tempoExpiracaoCarrinho.getDate() + 30)
  Cookie.set(SHOPPING_CART_COOKIE, id, {
    domain: getDomainName(),
    expires: tempoExpiracaoCarrinho,
  })
}

const MAX_QUANTITY_ALLOWED = 10

const useCarrinho = () => {
  const [carrinhoStore, setCarrinho] = useState<Carrinho | undefined>()
  const [loadingCarrinho, setLoadingCarrinho] = useState(true)

  useEffect(() => {
    iniciarCarrinho()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setarCarrinho = (response: Carrinho | null) => {
    if (!response?.id) {
      setCarrinho(undefined)
      return
    }

    setCarrinho({
      carrinhos_itens: [...response.carrinhos_itens],
      chave_canal: response.chave_canal,
      consumidor_id: response.consumidor_id,
      id: response.id,
      status: response.status,
      tipo: response.tipo,
    })
  }

  const iniciarCarrinho = () => {
    setLoadingCarrinho(true)

    getCarrinho()
      .then((response) => {
        setarCarrinho(response)
        salvarCarrinho(response?.id ?? '')
      })
      .finally(() => setLoadingCarrinho(false))
  }

  const onError = (
    error: any,
    defaultError = 'Erro ao processar a solicitação.'
  ) => {
    const msg =
      error?.response?.data?.message ||
      error?.response?.message ||
      error?.response?.data?.errors?.quantidade?.[0] ||
      defaultError

    if (error.response?.status === 404) {
      toast.error('O carrinho não pôde ser recuperado.')
      removerCarrinho()
      setarCarrinho(null)
      return
    }

    if (error?.response?.status === 422) {
      toast.warning(msg)
      return
    }

    toast.error(msg)
  }

  const getCarrinho = async () => {
    const usuarioToken = getToken()
    const idCarrinho = recuperarCarrinho()

    if (!usuarioToken && !idCarrinho) {
      return Promise.resolve(null)
    }

    const carrinhoRequest = usuarioToken
      ? apiV2.get<Carrinho>(`/canais/carrinho/usuario`)
      : apiV2.get<Carrinho>(`/canais/carrinho/${idCarrinho}`)

    return await carrinhoRequest
      .then((response) => response.data)
      .catch((error) => {
        if (error.response?.status === 403 && idCarrinho) {
          removerCarrinho()
          return null
        }

        Sentry.captureException(error)
        return null
      })
  }

  const adicionar = async (
    quantidade: number,
    idAnuncio: number,
    urlProduto: string,
    idProdutoGarantia?: number
  ) => {
    setLoadingCarrinho(true)

    let carrinhoLocal =
      Number(carrinhoStore?.id) === Number(recuperarCarrinho())
        ? carrinhoStore
        : null

    if (!carrinhoLocal?.id) {
      carrinhoLocal = await getCarrinho()
    }

    const produto = carrinhoLocal?.carrinhos_itens?.filter(
      (item) => item.anuncio_id === idAnuncio
    )[0]

    const item = {
      anuncio_id: idAnuncio,
      quantidade: quantidade,
      url_produto: urlProduto,
      produtos_garantias_id: idProdutoGarantia ? idProdutoGarantia : null,
    }

    let carrinhoRequest

    if (produto) {
      // incrementar quantidade
      if (produto.quantidade === MAX_QUANTITY_ALLOWED) {
        toast.error(`Você já atingiu a quantidade máxima permitida.`)
        return
      }

      if (produto.quantidade + quantidade > MAX_QUANTITY_ALLOWED) {
        // adiciona apenas a quantidade máxima permitida
        item.quantidade = MAX_QUANTITY_ALLOWED - produto.quantidade
      }

      carrinhoRequest = apiV2.put(
        `/canais/carrinho/${carrinhoLocal?.id}/incrementar-item`,
        item
      )
    } else if (carrinhoLocal?.id) {
      // adicionar item
      carrinhoRequest = apiV2.put(`/canais/carrinho/${carrinhoLocal.id}/`, item)
    } else {
      // criar carrinho e adicionar item
      carrinhoRequest = apiV2.post(`/canais/carrinho`, item)
    }

    await carrinhoRequest
      .then((response) => {
        if (isAxiosError(response)) {
          onError(response)
          return
        }

        const carrinho = response.data
        salvarCarrinho(carrinho.id)
        const produto = carrinho.carrinhos_itens.filter(
          (itemSalvo: any) => itemSalvo.anuncio_id === item.anuncio_id
        )[0]
        const objetoGoogle = montarObjetoGoogle(produto)

        gtg.addToCart(objetoGoogle.gtgProduto)
        gtm.addToCart(objetoGoogle.gtmProduto)
        setarCarrinho(carrinho)
      })
      .catch((error) => onError(error))
      .finally(() => setLoadingCarrinho(false))
  }

  const remover = (anuncioId: number) => {
    setLoadingCarrinho(true)

    apiV2
      .delete(`/canais/carrinho/${carrinhoStore?.id}/anuncio/${anuncioId}`)
      .then((response) => {
        if (!response.data.carrinhos_itens?.length) {
          removerCarrinho()
        }

        const produtoRemovido = carrinhoStore?.carrinhos_itens.filter(
          (removido) => removido.anuncio_id === anuncioId
        )[0]

        const objetoGoogle = montarObjetoGoogle(produtoRemovido)

        gtg.removeFromCart(objetoGoogle.gtgProduto)
        gtm.removeFromCart(objetoGoogle.gtmProduto)

        setarCarrinho(response.data)
        toast.success(`Produto removido!`)
      })
      .catch((error) => onError(error))
      .finally(() => setLoadingCarrinho(false))
  }

  const alterarGarantia = async (
    idAnuncio: number,
    produtoGarantiaId: number
  ) => {
    setLoadingCarrinho(true)

    const produto = carrinhoStore?.carrinhos_itens.filter(
      (item) => item.anuncio_id === idAnuncio
    )[0]

    if (!produto) return

    await alterarProduto(
      idAnuncio,
      produto.quantidade,
      produto.url_produto,
      produtoGarantiaId,
      'Erro ao alterar garantia!'
    )
  }

  const alterarProduto = async (
    anuncioId: number,
    quantidade: number,
    urlProduto: string,
    produtoGarantiaId: number,
    defaultError: any
  ) => {
    const item = {
      anuncio_id: anuncioId,
      quantidade: quantidade,
      url_produto: urlProduto,
      produtos_garantias_id: produtoGarantiaId || null,
    }

    if (Number(carrinhoStore?.id) != Number(recuperarCarrinho())) {
      await adicionar(quantidade, anuncioId, urlProduto, produtoGarantiaId)
      return
    }

    await apiV2
      .put(`/canais/carrinho/${carrinhoStore?.id}`, item)
      .then((response) => setarCarrinho(response.data))
      .catch((error) => onError(error, defaultError))
      .finally(() => setLoadingCarrinho(false))
  }

  const mergeCarrinho = () => {
    const carrinhoId = recuperarCarrinho()

    const request = carrinhoId
      ? apiV2.get(`/canais/carrinho/merge/${carrinhoId}`)
      : apiV2.get(`/canais/carrinho/usuario`)

    request
      .then((response) => {
        salvarCarrinho(response.data.id)
        setarCarrinho(response.data)
      })
      .catch((error) => {
        Sentry.captureException(error)
        toast.error(error.response.data.message)
      })
  }

  const limparCarrinho = () => {
    setarCarrinho(null)
  }

  return {
    loadingCarrinho,
    carrinhoStore,
    iniciarCarrinho,
    adicionar,
    remover,
    mergeCarrinho,
    alterarGarantia,
    limparCarrinho,
  }
}

export default useCarrinho
